'use client'

import React from 'react'
import {
  Code,
  Database,
  Server,
  Brain,
  Globe,
  Clock,
  ArrowRight,
  CheckCircle,
  XCircle,
  Layers,
  Rocket,
  Lock,
  Unlock,
  DollarSign,
  GitBranch,
  CloudCog,
} from 'lucide-react'

/**
 * Philippines AI Development Diagrams
 * Visual components for the AI product development Philippines CTO guide
 * Brand colors: Primary #008dcc, Accent #FDC304, Pink #F90B8A, Purple #8b5cf6
 */

/**
 * Ownership Model Comparison: Typical Offshore vs Pixelmojo Model
 */
export function OwnershipModelComparisonDiagram() {
  const typicalModel = [
    {
      label: 'Black-Box SaaS',
      sublabel: 'Vendor controls the platform',
      icon: Lock,
      color: '#ef4444',
    },
    {
      label: 'Per-Seat Pricing',
      sublabel: 'Costs scale with team size',
      icon: DollarSign,
      color: '#ef4444',
    },
    {
      label: 'Vendor Lock-In',
      sublabel: 'Migration costs keep you trapped',
      icon: XCircle,
      color: '#ef4444',
    },
    {
      label: 'Shared Infrastructure',
      sublabel: 'Your data on their servers',
      icon: Server,
      color: '#ef4444',
    },
  ]

  const pixelmojoModel = [
    {
      label: 'Full Source Code',
      sublabel: 'You own every line we write',
      icon: Unlock,
      color: '#22c55e',
    },
    {
      label: 'Flat Platform Fee',
      sublabel: 'Predictable monthly cost',
      icon: DollarSign,
      color: '#22c55e',
    },
    {
      label: 'Walk Away Anytime',
      sublabel: 'No lock-in, no exit fees',
      icon: CheckCircle,
      color: '#22c55e',
    },
    {
      label: 'Your Database',
      sublabel: 'Your infrastructure, your data',
      icon: Database,
      color: '#22c55e',
    },
  ]

  return (
    <div data-blog-layout='narrow' className='my-8'>
      <div className='bg-muted/50 rounded-2xl border border-border/60 p-6 md:p-8'>
        <h4 className='text-lg font-bold text-foreground mb-6 text-center'>
          The Ownership Divide: Typical Offshore vs. Pixelmojo Model
        </h4>

        <div className='grid md:grid-cols-2 gap-6'>
          {/* Typical Offshore Column */}
          <div className='bg-background rounded-xl border border-border/60 p-5'>
            <div
              className='text-center mb-4 pb-3 border-b'
              style={{ borderColor: '#ef444430' }}
            >
              <div
                className='inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-sm font-semibold'
                style={{ backgroundColor: '#ef444415', color: '#ef4444' }}
              >
                <Lock className='w-4 h-4' />
                Typical Offshore Model
              </div>
            </div>
            <div className='space-y-3'>
              {typicalModel.map((item, i) => {
                const Icon = item.icon
                return (
                  <div key={i} className='flex items-start gap-3'>
                    <div
                      className='w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 mt-0.5'
                      style={{
                        backgroundColor: `${item.color}15`,
                        color: item.color,
                      }}
                    >
                      <Icon className='w-4 h-4' />
                    </div>
                    <div>
                      <div className='text-sm font-semibold text-foreground'>
                        {item.label}
                      </div>
                      <div className='text-xs text-muted-foreground'>
                        {item.sublabel}
                      </div>
                    </div>
                  </div>
                )
              })}
            </div>
          </div>

          {/* Pixelmojo Column */}
          <div className='bg-background rounded-xl border border-border/60 p-5'>
            <div
              className='text-center mb-4 pb-3 border-b'
              style={{ borderColor: '#008dcc30' }}
            >
              <div
                className='inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-sm font-semibold'
                style={{ backgroundColor: '#008dcc15', color: '#008dcc' }}
              >
                <Unlock className='w-4 h-4' />
                Pixelmojo Model
              </div>
            </div>
            <div className='space-y-3'>
              {pixelmojoModel.map((item, i) => {
                const Icon = item.icon
                return (
                  <div key={i} className='flex items-start gap-3'>
                    <div
                      className='w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 mt-0.5'
                      style={{
                        backgroundColor: `${item.color}15`,
                        color: item.color,
                      }}
                    >
                      <Icon className='w-4 h-4' />
                    </div>
                    <div>
                      <div className='text-sm font-semibold text-foreground'>
                        {item.label}
                      </div>
                      <div className='text-xs text-muted-foreground'>
                        {item.sublabel}
                      </div>
                    </div>
                  </div>
                )
              })}
            </div>
          </div>
        </div>

        <div className='mt-5 text-center'>
          <p className='text-xs text-muted-foreground'>
            Ownership means you control your IP, your data, and your exit
            strategy.
          </p>
        </div>
      </div>
    </div>
  )
}

/**
 * Timezone Overlap Diagram: GMT+8 Philippines vs global regions
 */
export function TimezoneOverlapDiagram() {
  // Each bar is that market's 9AM-5PM (standard time) expressed in Manila
  // time (GMT+8), so the overlap with the 9AM-6PM Manila bar is what you see.
  const regions = [
    {
      name: 'US West (PT)',
      offset: 'GMT-8/-7',
      overlapLabel: 'No overlap (US evening and night)',
      color: '#008dcc',
      windowStart: 1,
      windowEnd: 9,
    },
    {
      name: 'US East (ET)',
      offset: 'GMT-5/-4',
      overlapLabel: 'No overlap (US evening and night)',
      color: '#8b5cf6',
      windowStart: 22,
      windowEnd: 30,
    },
    {
      name: 'UK / EU (GMT/CET)',
      offset: 'GMT+0/+1',
      overlapLabel: '1-2 hrs overlap (their morning)',
      color: '#FDC304',
      windowStart: 16,
      windowEnd: 24,
    },
    {
      name: 'ANZ (AEST)',
      offset: 'GMT+10/+11',
      overlapLabel: '5-6 hrs overlap',
      color: '#F90B8A',
      windowStart: 7,
      windowEnd: 15,
    },
  ]

  const phWorkStart = 9
  const phWorkEnd = 18

  return (
    <div data-blog-layout='narrow' className='my-8'>
      <div className='bg-muted/50 rounded-2xl border border-border/60 p-6 md:p-8'>
        <h4 className='text-lg font-bold text-foreground mb-2 text-center'>
          Philippines (GMT+8) Timezone Overlap
        </h4>
        <p className='text-sm text-muted-foreground text-center mb-6'>
          Each market's 9AM-5PM business day, shown in Philippines time
        </p>

        <div className='space-y-4'>
          {/* Philippines base row */}
          <div className='bg-background rounded-xl border border-border/60 p-4'>
            <div className='flex items-center gap-3 mb-3'>
              <div
                className='w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0'
                style={{ backgroundColor: '#22c55e15', color: '#22c55e' }}
              >
                <Globe className='w-4 h-4' />
              </div>
              <div>
                <div className='text-sm font-semibold text-foreground'>
                  Philippines (PHT)
                </div>
                <div className='text-xs text-muted-foreground'>
                  GMT+8 / 9AM - 6PM work hours
                </div>
              </div>
            </div>
            {/* 24-hour bar */}
            <div className='relative h-6 bg-muted rounded-full overflow-hidden'>
              <div
                className='absolute h-full rounded-full'
                style={{
                  left: `${(phWorkStart / 24) * 100}%`,
                  width: `${((phWorkEnd - phWorkStart) / 24) * 100}%`,
                  backgroundColor: '#22c55e30',
                  border: '1px solid #22c55e60',
                }}
              />
              {/* Hour markers */}
              {[0, 6, 12, 18].map(h => (
                <div
                  key={h}
                  className='absolute top-0 h-full border-l border-border/30'
                  style={{ left: `${(h / 24) * 100}%` }}
                />
              ))}
            </div>
            <div className='flex justify-between text-[10px] text-muted-foreground mt-1 px-1'>
              <span>12AM</span>
              <span>6AM</span>
              <span>12PM</span>
              <span>6PM</span>
              <span>12AM</span>
            </div>
          </div>

          {/* Region overlap rows */}
          {regions.map((region, i) => (
            <div
              key={i}
              className='bg-background rounded-xl border border-border/60 p-4'
            >
              <div className='flex items-center justify-between mb-3'>
                <div className='flex items-center gap-3'>
                  <div
                    className='w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0'
                    style={{
                      backgroundColor: `${region.color}15`,
                      color: region.color,
                    }}
                  >
                    <Clock className='w-4 h-4' />
                  </div>
                  <div>
                    <div className='text-sm font-semibold text-foreground'>
                      {region.name}
                    </div>
                    <div className='text-xs text-muted-foreground'>
                      {region.offset}
                    </div>
                  </div>
                </div>
                <div
                  className='text-xs font-semibold px-2 py-1 rounded-full'
                  style={{
                    backgroundColor: `${region.color}15`,
                    color: region.color,
                  }}
                >
                  {region.overlapLabel}
                </div>
              </div>
              {/* Overlap bar */}
              <div className='relative h-6 bg-muted rounded-full overflow-hidden'>
                {/* A window that runs past midnight draws as two segments. */}
                {(region.windowEnd > 24
                  ? [
                      [region.windowStart, 24],
                      [0, region.windowEnd - 24],
                    ]
                  : [[region.windowStart, region.windowEnd]]
                ).map(([start, end]) => (
                  <div
                    key={start}
                    className='absolute h-full rounded-full'
                    style={{
                      left: `${(start / 24) * 100}%`,
                      width: `${((end - start) / 24) * 100}%`,
                      backgroundColor: `${region.color}30`,
                      border: `1px solid ${region.color}60`,
                    }}
                  />
                ))}
                {[0, 6, 12, 18].map(h => (
                  <div
                    key={h}
                    className='absolute top-0 h-full border-l border-border/30'
                    style={{ left: `${(h / 24) * 100}%` }}
                  />
                ))}
              </div>
            </div>
          ))}
        </div>

        <div className='mt-5 text-center'>
          <p className='text-xs text-muted-foreground'>
            Hours shown in Philippines local time (GMT+8), standard time.
            Daylight saving shifts US, UK, EU and ANZ bars by an hour.
          </p>
        </div>
      </div>
    </div>
  )
}

/**
 * Engagement Pathway: 60-Day Sprint → Vector → Hive → Platform Fee
 */
export function EngagementPathwayDiagram() {
  const stages = [
    {
      stage: 'Prove',
      name: '60-Day Conversion Sprint',
      price: '$4,500',
      description: 'Validate the approach with a focused sprint',
      details: [
        'Conversion audit',
        'AI agent prototype',
        'Performance baseline',
      ],
      icon: Rocket,
      color: '#FDC304',
    },
    {
      stage: 'Build',
      name: 'Vector Build',
      price: '$35,000',
      description: 'Single AI product, fully owned',
      details: [
        'Lead qualification AI',
        'RAG system',
        'Full source code delivery',
      ],
      icon: Code,
      color: '#008dcc',
    },
    {
      stage: 'Scale',
      name: 'Hive Build',
      price: '$65,000',
      description: 'Multi-agent orchestration platform',
      details: [
        'Agent coordination',
        'Workflow automation',
        'Enterprise integrations',
      ],
      icon: Brain,
      color: '#8b5cf6',
    },
    {
      stage: 'Sustain',
      name: 'Platform Fee',
      price: '$1.5K - $3K/mo',
      description: 'Ongoing optimization and support',
      details: ['Model tuning', 'Performance monitoring', 'Feature iterations'],
      icon: Layers,
      color: '#22c55e',
    },
  ]

  return (
    <div data-blog-layout='narrow' className='my-8'>
      <div className='bg-muted/50 rounded-2xl border border-border/60 p-6 md:p-8'>
        <h4 className='text-lg font-bold text-foreground mb-2 text-center'>
          Engagement Pathway: Prove Before You Commit
        </h4>
        <p className='text-sm text-muted-foreground text-center mb-6'>
          Each phase delivers standalone value. No obligation to continue.
        </p>

        <div className='space-y-3'>
          {stages.map((stage, i) => {
            const Icon = stage.icon
            return (
              <React.Fragment key={i}>
                <div className='bg-background rounded-xl border border-border/60 p-5'>
                  <div className='flex items-start gap-4'>
                    <div className='flex flex-col items-center flex-shrink-0'>
                      <div
                        className='w-10 h-10 rounded-xl flex items-center justify-center'
                        style={{
                          backgroundColor: `${stage.color}15`,
                          color: stage.color,
                        }}
                      >
                        <Icon className='w-5 h-5' />
                      </div>
                      <div
                        className='text-[10px] font-bold mt-1 uppercase tracking-wider'
                        style={{ color: stage.color }}
                      >
                        {stage.stage}
                      </div>
                    </div>
                    <div className='flex-1 min-w-0'>
                      <div className='flex items-center justify-between flex-wrap gap-2'>
                        <div className='text-sm font-bold text-foreground'>
                          {stage.name}
                        </div>
                        <div
                          className='text-sm font-bold px-2.5 py-0.5 rounded-full'
                          style={{
                            backgroundColor: `${stage.color}15`,
                            color: stage.color,
                          }}
                        >
                          {stage.price}
                        </div>
                      </div>
                      <div className='text-xs text-muted-foreground mt-1'>
                        {stage.description}
                      </div>
                      <div className='flex flex-wrap gap-1.5 mt-2'>
                        {stage.details.map((detail, j) => (
                          <span
                            key={j}
                            className='text-[10px] px-2 py-0.5 rounded-full bg-muted text-foreground/80'
                          >
                            {detail}
                          </span>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>
                {i < stages.length - 1 && (
                  <div className='flex justify-center'>
                    <ArrowRight className='w-4 h-4 text-muted-foreground rotate-90' />
                  </div>
                )}
              </React.Fragment>
            )
          })}
        </div>

        <div className='mt-5 text-center'>
          <p className='text-xs text-muted-foreground'>
            All pricing is public. No hidden fees, no contract traps.
          </p>
        </div>
      </div>
    </div>
  )
}

/**
 * Capability Matrix: AI/ML, Full-Stack, Infrastructure, Methodology
 */
export function CapabilityMatrixDiagram() {
  const domains = [
    {
      name: 'AI / ML',
      icon: Brain,
      color: '#8b5cf6',
      capabilities: [
        { name: 'LLM Integration', level: 'Production' },
        { name: 'RAG Systems', level: 'Production' },
        { name: 'Multi-Agent Orchestration', level: 'Production' },
        { name: 'Prompt Engineering', level: 'Production' },
        { name: 'Vector Databases', level: 'Production' },
      ],
    },
    {
      name: 'Full-Stack',
      icon: Code,
      color: '#008dcc',
      capabilities: [
        { name: 'React / Next.js', level: 'Production' },
        { name: 'TypeScript', level: 'Production' },
        { name: 'Python / FastAPI', level: 'Production' },
        { name: 'PostgreSQL / Supabase', level: 'Production' },
        { name: 'Mobile (React Native)', level: 'Production' },
      ],
    },
    {
      name: 'Infrastructure',
      icon: CloudCog,
      color: '#FDC304',
      capabilities: [
        { name: 'AWS / Vercel', level: 'Production' },
        { name: 'Docker / Containers', level: 'Production' },
        { name: 'CI/CD Pipelines', level: 'Production' },
        { name: 'Edge Computing', level: 'Production' },
        { name: 'Monitoring / Observability', level: 'Production' },
      ],
    },
    {
      name: 'Methodology',
      icon: GitBranch,
      color: '#F90B8A',
      capabilities: [
        { name: 'Thread-Based Engineering', level: 'Proprietary' },
        { name: 'AI Governance', level: 'Built-In' },
        { name: 'Security-First Development', level: 'Standard' },
        { name: 'Continuous Delivery', level: 'Standard' },
        { name: 'Code Ownership Transfer', level: 'Standard' },
      ],
    },
  ]

  return (
    <div data-blog-layout='narrow' className='my-8'>
      <div className='bg-muted/50 rounded-2xl border border-border/60 p-6 md:p-8'>
        <h4 className='text-lg font-bold text-foreground mb-2 text-center'>
          Capability Matrix
        </h4>
        <p className='text-sm text-muted-foreground text-center mb-6'>
          What a methodology-driven AI team actually delivers
        </p>

        <div className='grid md:grid-cols-2 gap-4'>
          {domains.map((domain, i) => {
            const Icon = domain.icon
            return (
              <div
                key={i}
                className='bg-background rounded-xl border border-border/60 p-4'
              >
                <div className='flex items-center gap-2.5 mb-3 pb-2 border-b border-border/40'>
                  <div
                    className='w-8 h-8 rounded-lg flex items-center justify-center'
                    style={{
                      backgroundColor: `${domain.color}15`,
                      color: domain.color,
                    }}
                  >
                    <Icon className='w-4 h-4' />
                  </div>
                  <div
                    className='text-sm font-bold'
                    style={{ color: domain.color }}
                  >
                    {domain.name}
                  </div>
                </div>
                <div className='space-y-2'>
                  {domain.capabilities.map((cap, j) => (
                    <div key={j} className='flex items-center justify-between'>
                      <span className='text-xs text-foreground/80'>
                        {cap.name}
                      </span>
                      <span
                        className='text-[10px] font-semibold px-2 py-0.5 rounded-full'
                        style={{
                          backgroundColor: `${domain.color}15`,
                          color: domain.color,
                        }}
                      >
                        {cap.level}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            )
          })}
        </div>

        <div className='mt-5 text-center'>
          <p className='text-xs text-muted-foreground'>
            All capabilities are production-tested across shipped products, not
            theoretical.
          </p>
        </div>
      </div>
    </div>
  )
}
